import React, { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TablePagination,
  CircularProgress,
  TextField,
  Button,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  IconButton,
  Chip
} from '@mui/material';
import { Add, Remove, ExpandMore } from '@mui/icons-material';
import api from '../services/api';
import { Visit, FinancialOverview } from '../types';

// Helper to get all dates in a range (inclusive)
function getAllDatesInRange(from: string, to: string): string[] {
  const result: string[] = [];
  const start = new Date(from);
  const end = new Date(to);
  // Normalize to midnight
  start.setHours(0, 0, 0, 0);
  end.setHours(0, 0, 0, 0);
  for (let d = new Date(start); d <= end; d.setDate(d.getDate() + 1)) {
    // Format as yyyy-mm-dd
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    result.push(`${year}-${month}-${day}`);
  }
  return result;
}

function getCurrentMonthRange() {
  const now = new Date();
  const firstDay = new Date(now.getFullYear(), now.getMonth(), 1);
  const today = new Date();
  const pad = (n: number) => String(n).padStart(2, '0');
  return {
    from: firstDay.getFullYear() + '-' + pad(firstDay.getMonth() + 1) + '-' + pad(firstDay.getDate()), // 1st of current month
    to: today.getFullYear() + '-' + pad(today.getMonth() + 1) + '-' + pad(today.getDate()),
  };
}

const { from: initialFrom, to: initialTo } = getCurrentMonthRange();

const MIN_ROWS_PER_PAGE = 32;

const Accounts: React.FC = () => {
  const [datewiseAccounts, setDatewiseAccounts] = useState<any[]>([]);
  const [paymentModes, setPaymentModes] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(0);
  // Set minimum 32 rows per page
  const [rowsPerPage, setRowsPerPage] = useState(MIN_ROWS_PER_PAGE);
  const [totalDates, setTotalDates] = useState(0);
  const [overview, setOverview] = useState<FinancialOverview | null>(null);
  const [dateFrom, setDateFrom] = useState(initialFrom);
  const [dateTo, setDateTo] = useState(initialTo);

  // This will store the full list of all dates in the range, for pagination
  const [allDatesInRange, setAllDatesInRange] = useState<string[]>([]);

  // Map of datevisit to account data for quick lookup
  const [dateAccountMap, setDateAccountMap] = useState<Record<string, any>>({});
  
  // Track which accordion dates are expanded
  const [expandedDates, setExpandedDates] = useState<Set<string>>(new Set());
  
  // Store detailed patient visits for each date
  const [dateVisitDetails, setDateVisitDetails] = useState<Record<string, any[]>>({});
  
  // Loading state for individual accordion rows
  const [loadingAccordions, setLoadingAccordions] = useState<Set<string>>(new Set());

  // When dateFrom/dateTo changes, recalculate allDatesInRange
  useEffect(() => {
    const allDates = getAllDatesInRange(dateFrom, dateTo);
    setAllDatesInRange(allDates);
    setPage(0); // Reset to first page on date change
  }, [dateFrom, dateTo]);

  // Fetch accounts and payment modes, and build map
  const fetchDatewiseAccounts = async () => {
    setLoading(true);
    try {
      // Fetch all data for the full date range (no pagination at API level)
      const res = await api.getDatewiseAccounts({ page: 1, limit: 10000, dateFrom, dateTo });
      const accounts = res.data?.datewiseAccounts || [];
      setPaymentModes(res.data?.paymentModes || []);
      // Build map for quick lookup
      const map: Record<string, any> = {};
      for (const acc of accounts) {
        // Normalize datevisit to yyyy-mm-dd
        let dateKey = acc.datevisit;
        if (dateKey && dateKey.length > 10) dateKey = dateKey.slice(0, 10);
        map[dateKey] = acc;
      }
      setDateAccountMap(map);
      setLoading(false);
    } catch (err) {
      console.error('Error fetching datewise accounts:', err);
      setDateAccountMap({});
      setPaymentModes([]);
      setLoading(false);
    }
  };

  const fetchOverview = async () => {
    try {
      const res = await api.getFinancialOverview(dateFrom, dateTo);
      setOverview(res.data || null);
    } catch (err) {
      setOverview(null);
    }
  };

  // Toggle accordion expansion
  const toggleAccordion = async (uniqueKey: string) => {
    const isExpanded = expandedDates.has(uniqueKey);
    
    if (isExpanded) {
      // Collapse accordion
      setExpandedDates(prev => {
        const newSet = new Set(prev);
        newSet.delete(uniqueKey);
        return newSet;
      });
    } else {
      // Expand accordion and fetch details if not already loaded
      setExpandedDates(prev => {
        const newSet = new Set(prev);
        newSet.add(uniqueKey);
        return newSet;
      });
      
      // Extract dateKey from uniqueKey (remove the -idx part)
      const dateKey = uniqueKey.split('-').slice(0, 3).join('-');
      
      if (!dateVisitDetails[dateKey]) {
        setLoadingAccordions(prev => new Set(prev).add(uniqueKey));
        await fetchDateVisitDetails(dateKey);
        setLoadingAccordions(prev => {
          const newSet = new Set(prev);
          newSet.delete(uniqueKey);
          return newSet;
        });
      }
    }
  };

  // Fetch detailed patient visits for a specific date
  const fetchDateVisitDetails = async (dateKey: string) => {
    try {
      // Use the same date for both dateFrom and dateTo to get visits for that specific date
      const response = await api.getVisits({
        page: 1,
        limit: 1000, // Get all visits for the date
        dateFrom: dateKey,
        dateTo: dateKey
      });
      
      if (response.success && response.data) {
        const visits = response.data?.visits || [];
        
        setDateVisitDetails(prev => ({
          ...prev,
          [dateKey]: visits
        }));
      }
    } catch (error) {
      console.error('Error fetching date visit details:', error);
      setDateVisitDetails(prev => ({
        ...prev,
        [dateKey]: []
      }));
    }
  };

  // Helper function to get the date key for consistent mapping
  const getDateKey = (dateString: string) => {
    try {
      const date = new Date(dateString);
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      return `${year}-${month}-${day}`;
    } catch (error) {
      return dateString;
    }
  };

  // When dateFrom/dateTo changes, fetch new data
  useEffect(() => {
    fetchDatewiseAccounts();
    fetchOverview();
    // eslint-disable-next-line
  }, [dateFrom, dateTo]);

  // When allDatesInRange or dateAccountMap or pagination changes, update paginated datewiseAccounts
  useEffect(() => {
    // Paginate allDatesInRange
    const startIdx = page * rowsPerPage;
    const endIdx = startIdx + rowsPerPage;
    const pagedDates = allDatesInRange.slice(startIdx, endIdx);

    // For each date, get account or fill with zeros
    const pagedAccounts = pagedDates.map(date => {
      const acc = dateAccountMap[date];
      if (acc) {
        return acc;
      } else {
        // Fill with zeros and empty values
        const paymentModeFields: Record<string, number> = {};
        paymentModes.forEach(mode => {
          const key = mode.toLowerCase().replace(/\s+/g, '_') + '_revenue';
          paymentModeFields[key] = 0;
        });
        return {
          datevisit: date,
          unique_patients: 0,
          total_fee: 0,
          total_balance: 0,
          total_credit: 0,
          ...paymentModeFields,
        };
      }
    });
    setDatewiseAccounts(pagedAccounts);
    setTotalDates(allDatesInRange.length);
  }, [allDatesInRange, dateAccountMap, paymentModes, page, rowsPerPage]);

  const formatCurrency = (amount: string | number) => {
    const num = parseFloat(amount?.toString() || '0');
    return `₹${num % 1 === 0 ? num.toFixed(0) : num.toFixed(2)}`;
  };

  const formatDate = (dateString: string) => {
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-GB');
    } catch (error) {
      return dateString;
    }
  };

  const formatDateTime = (dateString: string, timeString: string) => {
    try {
      const date = new Date(dateString);
      const formattedDate = date.toLocaleDateString('en-GB');
      const formattedTime = timeString || '';
      return `${formattedDate} ${formattedTime}`;
    } catch (error) {
      return `${dateString} ${timeString || ''}`;
    }
  };

  // For summary row, sum over all dates in range (not just current page)
  const getSummaryTotals = () => {
    let total_fee = 0;
    let total_balance = 0;
    let total_credit = 0;
    const paymentModeTotals: Record<string, number> = {};
    paymentModes.forEach(mode => {
      const key = mode.toLowerCase().replace(/\s+/g, '_') + '_revenue';
      paymentModeTotals[key] = 0;
    });

    allDatesInRange.forEach(date => {
      const acc = dateAccountMap[date];
      if (acc) {
        total_fee += parseFloat(acc.total_fee || 0);
        total_balance += parseFloat(acc.total_balance || 0);
        total_credit += parseFloat(acc.total_credit || 0);
        paymentModes.forEach(mode => {
          const key = mode.toLowerCase().replace(/\s+/g, '_') + '_revenue';
          paymentModeTotals[key] += parseFloat(acc[key] || 0);
        });
      } else {
        // No data for this date, so add zeros (already default)
      }
    });

    return { total_fee, total_balance, total_credit, paymentModeTotals };
  };

  const summaryTotals = getSummaryTotals();

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" component="h1" sx={{ mb: 3 }}>
        Date-wise Consolidated Accounts
      </Typography>

      {/* Date Range Filter */}
      <Box sx={{ display: 'flex', gap: 2, mb: 3, flexWrap: 'wrap' }}>
        <TextField
          label="From"
          type="date"
          value={dateFrom}
          onChange={e => setDateFrom(e.target.value)}
          InputLabelProps={{ shrink: true }}
          size="small"
        />
        <TextField
          label="To"
          type="date"
          value={dateTo}
          onChange={e => setDateTo(e.target.value)}
          InputLabelProps={{ shrink: true }}
          size="small"
        />
        <Button variant="outlined" onClick={() => { setDateFrom(initialFrom); setDateTo(initialTo); }}>This Month</Button>
      </Box>

      {/* Financial Summary */}
      <Box sx={{ display: 'flex', gap: 3, mb: 3, flexWrap: 'wrap' }}>
        <Card sx={{ minWidth: 200, flex: 1 }}>
          <CardContent>
            <Typography variant="subtitle2" color="text.secondary">Total Income</Typography>
            <Typography variant="h5" sx={{ fontWeight: 700, color: '#2e7d32' }}>
              {overview ? formatCurrency(overview.totalRevenue) : '--'}
            </Typography>
          </CardContent>
        </Card>
        <Card sx={{ minWidth: 200, flex: 1 }}>
          <CardContent>
            <Typography variant="subtitle2" color="text.secondary">Total Balance</Typography>
            <Typography variant="h5" sx={{ fontWeight: 700, color: '#d32f2f' }}>
              {overview ? formatCurrency(overview.totalBalance) : '--'}
            </Typography>
          </CardContent>
        </Card>
        <Card sx={{ minWidth: 200, flex: 1 }}>
          <CardContent>
            <Typography variant="subtitle2" color="text.secondary">Total Credit</Typography>
            <Typography variant="h5" sx={{ fontWeight: 700, color: '#1976d2' }}>
              {overview ? formatCurrency(overview.totalCredit) : '--'}
            </Typography>
          </CardContent>
        </Card>
      </Box>

      {/* Date-wise Consolidated Accounts Table */}
      <Card>
        <CardContent>
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          ) : (
            <TableContainer 
              component={Paper} 
              sx={{ 
                maxHeight: '70vh',
                border: '1px solid #e0e0e0',
                borderRadius: 2,
                boxShadow: 2
              }}
            >
              <Table stickyHeader>
                <TableHead>
                  <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                    <TableCell sx={{ fontWeight: 'bold', fontSize: '14px' }}>#</TableCell>
                    <TableCell sx={{ fontWeight: 'bold', fontSize: '14px', minWidth: '120px' }}>Date</TableCell>
                    {/* Removed Visits column */}
                    <TableCell sx={{ fontWeight: 'bold', fontSize: '14px', textAlign: 'center' }}>Patients</TableCell>
                    <TableCell sx={{ fontWeight: 'bold', fontSize: '14px', textAlign: 'right', color: '#2e7d32' }}>Total Income</TableCell>
                    <TableCell sx={{ fontWeight: 'bold', fontSize: '14px', textAlign: 'right', color: '#d32f2f' }}>Pending Balance</TableCell>
                    <TableCell sx={{ fontWeight: 'bold', fontSize: '14px', textAlign: 'right', color: '#1976d2' }}>Credit Given</TableCell>
                    {paymentModes && paymentModes.length > 0 ? paymentModes.map((mode, index) => (
                      <TableCell 
                        key={mode} 
                        sx={{ 
                          fontWeight: 'bold', 
                          fontSize: '14px', 
                          textAlign: 'right',
                          color: ['#4caf50', '#ff9800', '#9c27b0', '#607d8b', '#e91e63', '#795548'][index % 6]
                        }}
                      >
                        {mode}
                      </TableCell>
                    )) : (
                      <TableCell sx={{ fontWeight: 'bold', fontSize: '14px', textAlign: 'center', color: '#666' }}>
                        No Payment Data
                      </TableCell>
                    )}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {datewiseAccounts.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={6 + (paymentModes?.length || 0)} align="center">
                        No data found for the selected date range.
                      </TableCell>
                    </TableRow>
                  ) : (
                    datewiseAccounts.map((account, idx) => {
                      const dateKey = getDateKey(account.datevisit);
                      const uniqueKey = `${dateKey}-${idx}`; // Create unique key for each row
                      const isExpanded = expandedDates.has(uniqueKey);
                      const visitDetails = dateVisitDetails[dateKey] || [];
                      const isLoadingDetails = loadingAccordions.has(uniqueKey);
                      
                      return (
                        <React.Fragment key={uniqueKey}>
                          {/* Main summary row */}
                          <TableRow 
                            sx={{ 
                              '&:hover': { backgroundColor: '#f8f9fa' },
                              '&:nth-of-type(even)': { backgroundColor: '#fafafa' },
                              cursor: 'pointer'
                            }}
                            onClick={() => toggleAccordion(uniqueKey)}
                          >
                            <TableCell sx={{ fontWeight: '500' }}>{page * rowsPerPage + idx + 1}</TableCell>
                            <TableCell sx={{ fontWeight: '600', color: '#1976d2' }}>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                {formatDate(account.datevisit)}
                                <IconButton 
                                  size="small" 
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    toggleAccordion(uniqueKey);
                                  }}
                                  sx={{ 
                                    color: isExpanded ? '#1976d2' : '#666',
                                    transition: 'all 0.2s ease'
                                  }}
                                >
                                  {isExpanded ? <Remove /> : <Add />}
                                </IconButton>
                              </Box>
                            </TableCell>
                            <TableCell sx={{ textAlign: 'center', fontWeight: '500' }}>
                              {account.unique_patients}
                            </TableCell>
                            <TableCell sx={{ textAlign: 'right', fontWeight: '600', color: '#2e7d32', fontSize: '14px' }}>
                              {formatCurrency(account.total_fee)}
                            </TableCell>
                            <TableCell sx={{ textAlign: 'right', fontWeight: '500', color: '#d32f2f' }}>
                              {formatCurrency(account.total_balance)}
                            </TableCell>
                            <TableCell sx={{ textAlign: 'right', fontWeight: '500', color: parseFloat(account.total_credit || '0') > 0 ? '#1976d2' : '#2e7d32' }}>
                              {formatCurrency(account.total_credit)}
                            </TableCell>
                            {paymentModes && paymentModes.length > 0 ? paymentModes.map((mode, modeIndex) => {
                              const revenueKey = mode.toLowerCase().replace(/\s+/g, '_') + '_revenue';
                              return (
                                <TableCell 
                                  key={mode} 
                                  sx={{ 
                                    textAlign: 'right', 
                                    fontWeight: '500',
                                    color: ['#4caf50', '#ff9800', '#9c27b0', '#607d8b', '#e91e63', '#795548'][modeIndex % 6]
                                  }}
                                >
                                  {formatCurrency(account[revenueKey] || 0)}
                                </TableCell>
                              );
                            }) : (
                              <TableCell sx={{ textAlign: 'center', fontWeight: '500', color: '#666' }}>
                                -
                              </TableCell>
                            )}
                          </TableRow>
                          
                          {/* Expanded details row */}
                          {isExpanded && (
                            <TableRow>
                              <TableCell colSpan={6 + (paymentModes?.length || 0)} sx={{ p: 0, border: 'none' }}>
                                <Box sx={{ 
                                  backgroundColor: '#f8f9fa', 
                                  borderLeft: '4px solid #1976d2',
                                  p: 2
                                }}>
                                  <Typography variant="h6" sx={{ mb: 2, fontWeight: 600, color: '#1976d2' }}>
                                    Patient Visit Details for {formatDate(account.datevisit)}
                                  </Typography>
                                  
                                  {isLoadingDetails ? (
                                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
                                      <CircularProgress size={24} />
                                      <Typography variant="body2" sx={{ ml: 1 }}>
                                        Loading visit details...
                                      </Typography>
                                    </Box>
                                  ) : visitDetails.length > 0 ? (
                                    <TableContainer component={Paper} sx={{ boxShadow: 1 }}>
                                      <Table size="small">
                                        <TableHead>
                                          <TableRow sx={{ backgroundColor: '#e3f2fd' }}>
                                            <TableCell sx={{ fontWeight: 'bold' }}>Patient Name</TableCell>
                                            <TableCell sx={{ fontWeight: 'bold' }}>OPD</TableCell>
                                            <TableCell sx={{ fontWeight: 'bold' }}>Time</TableCell>
                                            <TableCell sx={{ fontWeight: 'bold' }}>Complaint</TableCell>
                                            <TableCell sx={{ fontWeight: 'bold' }}>Treatment</TableCell>
                                            <TableCell sx={{ fontWeight: 'bold' }}>Fee</TableCell>
                                            <TableCell sx={{ fontWeight: 'bold' }}>Balance</TableCell>
                                            <TableCell sx={{ fontWeight: 'bold' }}>Credit</TableCell>
                                            <TableCell sx={{ fontWeight: 'bold' }}>Payment Mode</TableCell>
                                            <TableCell sx={{ fontWeight: 'bold' }}>Follow Up</TableCell>
                                          </TableRow>
                                        </TableHead>
                                        <TableBody>
                                          {visitDetails.map((visit, visitIdx) => (
                                            <TableRow key={visit.id || visitIdx}>
                                              <TableCell sx={{ fontWeight: '500' }}>
                                                {visit.patient_name || 'N/A'}
                                              </TableCell>
                                              <TableCell sx={{ fontWeight: '500', color: '#1976d2' }}>
                                                {visit.opd || 'N/A'}
                                              </TableCell>
                                              <TableCell>
                                                {visit.time || 'N/A'}
                                              </TableCell>
                                              <TableCell sx={{ maxWidth: 200, wordWrap: 'break-word' }}>
                                                {visit.complaint || 'N/A'}
                                              </TableCell>
                                              <TableCell sx={{ maxWidth: 200, wordWrap: 'break-word' }}>
                                                {visit.treatment || 'N/A'}
                                              </TableCell>
                                              <TableCell sx={{ textAlign: 'right', fontWeight: '600', color: '#2e7d32' }}>
                                                {formatCurrency(visit.fee || 0)}
                                              </TableCell>
                                              <TableCell sx={{ textAlign: 'right', fontWeight: '500', color: '#d32f2f' }}>
                                                {formatCurrency(visit.balance || 0)}
                                              </TableCell>
                                              <TableCell sx={{ textAlign: 'right', fontWeight: '500', color: parseFloat(visit.credit || '0') > 0 ? '#1976d2' : '#2e7d32' }}>
                                                {formatCurrency(visit.credit || 0)}
                                              </TableCell>
                                              <TableCell>
                                                <Chip 
                                                  label={visit.payment_mode || 'N/A'} 
                                                  size="small"
                                                  color="primary"
                                                  variant="outlined"
                                                />
                                              </TableCell>
                                              <TableCell>
                                                {visit.follow ? (
                                                  <Chip 
                                                    label={formatDate(visit.follow)} 
                                                    size="small"
                                                    color="secondary"
                                                    variant="outlined"
                                                  />
                                                ) : (
                                                  <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                                                    No follow-up
                                                  </Typography>
                                                )}
                                              </TableCell>
                                            </TableRow>
                                          ))}
                                        </TableBody>
                                      </Table>
                                    </TableContainer>
                                  ) : (
                                    <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic', textAlign: 'center', py: 2 }}>
                                      No visit details found for this date.
                                    </Typography>
                                  )}
                                </Box>
                              </TableCell>
                            </TableRow>
                          )}
                        </React.Fragment>
                      );
                    })
                  )}
                  
                  {/* Summary Row */}
                  {datewiseAccounts.length > 0 && (
                    <TableRow sx={{ backgroundColor: '#e3f2fd', borderTop: '2px solid #1976d2' }}>
                      <TableCell sx={{ fontWeight: 'bold', fontSize: '16px' }} colSpan={3}>
                        <strong>Total for Selected Period</strong>
                      </TableCell>
                      <TableCell sx={{ textAlign: 'right', fontWeight: 'bold', fontSize: '16px', color: '#2e7d32' }}>
                        {formatCurrency(summaryTotals.total_fee)}
                      </TableCell>
                      <TableCell sx={{ textAlign: 'right', fontWeight: 'bold', fontSize: '16px', color: '#d32f2f' }}>
                        {formatCurrency(summaryTotals.total_balance)}
                      </TableCell>
                      <TableCell sx={{ textAlign: 'right', fontWeight: 'bold', fontSize: '16px', color: parseFloat(String(summaryTotals.total_credit || '0')) > 0 ? '#1976d2' : '#2e7d32' }}>
                        {formatCurrency(summaryTotals.total_credit)}
                      </TableCell>
                      {paymentModes && paymentModes.length > 0 ? paymentModes.map((mode, modeIndex) => {
                        const revenueKey = mode.toLowerCase().replace(/\s+/g, '_') + '_revenue';
                        return (
                          <TableCell 
                            key={mode} 
                            sx={{ 
                              textAlign: 'right', 
                              fontWeight: 'bold', 
                              fontSize: '16px',
                              color: ['#4caf50', '#ff9800', '#9c27b0', '#607d8b', '#e91e63', '#795548'][modeIndex % 6]
                            }}
                          >
                            {formatCurrency(summaryTotals.paymentModeTotals[revenueKey] || 0)}
                          </TableCell>
                        );
                      }) : (
                        <TableCell sx={{ textAlign: 'center', fontWeight: 'bold', fontSize: '16px', color: '#666' }}>
                          -
                        </TableCell>
                      )}
                      </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          )}
          <TablePagination
            rowsPerPageOptions={[MIN_ROWS_PER_PAGE, 50, 100]}
            component="div"
            count={totalDates}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={(_, newPage) => setPage(newPage)}
            onRowsPerPageChange={(e) => {
              const value = parseInt(e.target.value, 10);
              setRowsPerPage(value < MIN_ROWS_PER_PAGE ? MIN_ROWS_PER_PAGE : value);
              setPage(0);
            }}
          />
        </CardContent>
      </Card>
    </Box>
  );
};

export default Accounts; 